import {
  useInfiniteQuery,
  useQuery,
  type QueryClient,
} from "@tanstack/react-query";
import { DEFAULT_LOCALE, type Locale } from "@thyra/landing-i18n";
import {
  getPost,
  listPostLinks,
  listPosts,
  listLangTags,
  listTranslations,
  pickRelated,
  type BlogPage,
  type BlogPostLink,
  type BlogPostSummary,
} from "./ghost";
import type { TranslationLink } from "./blog-lang";

export const BLOG_PAGE_SIZE = 9;
export const LATEST_COUNT = 3;

/** Query keys shared by the hooks, the prerender preload and the tests. */
export const blogKeys = {
  latest: (locale: Locale) => ["blog", "latest", locale] as const,
  posts: (locale: Locale, tag?: string) =>
    ["blog", "posts", locale, tag ?? null] as const,
  /** A post is looked up by slug alone: its language is its own. */
  post: (slug: string) => ["blog", "post", slug] as const,
  related: (slug: string) => ["blog", "related", slug] as const,
  tags: (locale: Locale) => ["blog", "tags", locale] as const,
  links: (locale: Locale, tag?: string) =>
    ["blog", "links", locale, tag ?? null] as const,
  translations: (key: string) => ["blog", "translations", key] as const,
};

/**
 * A page of posts for a locale. `lang` is the language of the posts listed;
 * `fallback` is true when the locale has none and the English ones are shown
 * instead (the UI then says so and marks the content lang="en").
 */
export type BlogListPage = BlogPage & { lang: Locale; fallback: boolean };

export type PostsPageParam = { page: number; fallback: boolean };
export const FIRST_POSTS_PARAM: PostsPageParam = { page: 1, fallback: false };

/**
 * Posts of `locale`; when it has none (first page only) the English posts,
 * flagged `fallback`. Later pages stay in the language of the first.
 */
export async function fetchPostsPage({
  locale,
  tag,
  param,
  limit,
  signal,
}: {
  locale: Locale;
  tag?: string;
  param: PostsPageParam;
  limit: number;
  signal?: AbortSignal;
}): Promise<BlogListPage> {
  const lang = param.fallback ? DEFAULT_LOCALE : locale;
  const result = await listPosts({
    lang,
    tag,
    limit,
    page: param.page,
    signal,
  });
  if (
    !param.fallback &&
    param.page === 1 &&
    locale !== DEFAULT_LOCALE &&
    result.posts.length === 0
  ) {
    const english = await listPosts({
      lang: DEFAULT_LOCALE,
      tag,
      limit,
      page: 1,
      signal,
    });
    return { ...english, lang: DEFAULT_LOCALE, fallback: true };
  }
  return { ...result, lang, fallback: param.fallback };
}

/* ------------------------------------------------------------------ */
/* Build-time data handed to the browser                               */
/* ------------------------------------------------------------------ */

export type PreloadEntry = [key: readonly unknown[], data: unknown];

declare global {
  interface Window {
    __BLOG_PRELOAD__?: PreloadEntry[];
  }
}

/** JSON for an inline <script>: `<` and line separators cannot break out. */
export function serializePreload(entries: PreloadEntry[]): string {
  return JSON.stringify(entries)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

/**
 * Seeds the cache with the data the page was prerendered from (server and
 * browser share this, so hydration matches). `updatedAt: 0` marks it stale,
 * so the browser still refetches and shows posts published after the build.
 */
export function seedPreload(queryClient: QueryClient, entries: PreloadEntry[]) {
  for (const [key, data] of entries) {
    queryClient.setQueryData(key, data, { updatedAt: 0 });
  }
}

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

export function useLatestPosts(locale: Locale) {
  return useQuery({
    queryKey: blogKeys.latest(locale),
    queryFn: async ({ signal }) =>
      (
        await fetchPostsPage({
          locale,
          param: FIRST_POSTS_PARAM,
          limit: LATEST_COUNT,
          signal,
        })
      ).posts,
  });
}

export type PostsInfinite = {
  pages: BlogListPage[];
  pageParams: PostsPageParam[];
};

export function useBlogPosts(locale: Locale, tag?: string) {
  return useInfiniteQuery({
    queryKey: blogKeys.posts(locale, tag),
    initialPageParam: FIRST_POSTS_PARAM,
    queryFn: ({ pageParam, signal }) =>
      fetchPostsPage({
        locale,
        tag,
        param: pageParam,
        limit: BLOG_PAGE_SIZE,
        signal,
      }),
    getNextPageParam: (last): PostsPageParam | undefined =>
      last.pagination.next
        ? { page: last.pagination.next, fallback: last.fallback }
        : undefined,
  });
}

export type PostLinks = { links: BlogPostLink[]; fallback: boolean };

/** Every post title + slug, so older posts are crawlable without "Load more". */
export function usePostLinks(locale: Locale, tag?: string) {
  return useQuery<PostLinks>({
    queryKey: blogKeys.links(locale, tag),
    queryFn: async ({ signal }) => {
      const own = await listPostLinks({ lang: locale, tag, signal });
      if (own.length > 0 || locale === DEFAULT_LOCALE) {
        return { links: own, fallback: false };
      }
      return {
        links: await listPostLinks({ lang: DEFAULT_LOCALE, tag, signal }),
        fallback: true,
      };
    },
  });
}

/** Tag chips: tags of the posts the locale lists (its own, else English). */
export function useBlogTags(locale: Locale) {
  return useQuery({
    queryKey: blogKeys.tags(locale),
    queryFn: async ({ signal }) => {
      const own = await listLangTags(locale, signal);
      if (own.length > 0 || locale === DEFAULT_LOCALE) return own;
      return listLangTags(DEFAULT_LOCALE, signal);
    },
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: blogKeys.post(slug),
    queryFn: ({ signal }) => getPost(slug, signal),
    retry: 1,
  });
}

/** Other-language versions of a post (`#t-<key>`), itself included. */
export function useTranslations(current: BlogPostSummary | null | undefined) {
  const key = current?.translationKey ?? "";
  return useQuery<TranslationLink[]>({
    queryKey: blogKeys.translations(key),
    enabled: Boolean(key),
    queryFn: ({ signal }) => listTranslations(key, signal),
  });
}

/** Related posts for the end of an article, in the article's own language. */
export function useRelatedPosts(current: BlogPostSummary | null | undefined) {
  const slug = current?.slug ?? "";
  const tag = current?.primaryTag?.slug;
  const lang = current?.lang ?? DEFAULT_LOCALE;
  return useQuery({
    queryKey: blogKeys.related(slug),
    enabled: Boolean(current),
    queryFn: async ({ signal }) => {
      const [sameTag, latest] = await Promise.all([
        tag
          ? listPosts({ lang, limit: LATEST_COUNT + 1, tag, signal })
          : Promise.resolve(null),
        listPosts({ lang, limit: LATEST_COUNT + 1, signal }),
      ]);
      return pickRelated(current as BlogPostSummary, [
        ...(sameTag?.posts ?? []),
        ...latest.posts,
      ]);
    },
  });
}
